import { app } from "modular-openscriptjs";
import { authenticated, fireEvent, afterAuth, inform, redirect, empty, requireAuth, restorePreviousActiveNav } from "../helpers";
import { STRINGS, gc, uc } from "../declarations";
import { Preferences } from "@capacitor/preferences";

/**
 * The last page that actually rendered (URL + the router's resolved route
 * pattern). When a guest opens an account-only page we put them back
 * here - without re-rendering it - and show the login modal on top,
 * instead of navigating away to the login page.
 */
let lastRenderedRoute = null;

app("broker").on("ojs:routeChanged", () => {
	lastRenderedRoute = {
		url: `${location.pathname}${location.search}${location.hash}`,
		resolved: app("router").__resolved,
	};
});

/**
 * Returned by auth() (when called without a callback) for guests, so
 * chained calls like `middleware.auth().profileCompletion(...)` don't
 * throw - every step just stops.
 */
const blockedChain = {
	auth: () => blockedChain,
	profileCompletion: () => null,
	deliveryAddress: () => null,
};

export class Middleware {
	/**
	 * Ensures the user is authenticated.
	 *
	 * Guests can browse most of the app; for account-only pages this
	 * keeps them on the page they came from and pops up the login
	 * modal/offcanvas (see LoginModal). Once they sign in they're taken to
	 * the page they asked for (Auth.redirectIfNeeded()).
	 * @param {function} next
	 * @returns {any}
	 */
	auth(next) {
		if (authenticated()) {
			if (typeof next === "undefined") {
				return this;
			} else if (typeof next === "function") {
				return next();
			} else {
				return true;
			}
		}

		let url = `${location.pathname}${location.search}`;
		Preferences.set({ key: "redirect-url-after-auth", value: url });

		this.stayOnPreviousPage(url);
		requireAuth();

		return typeof next === "undefined" ? blockedChain : null;
	}

	/**
	 * Undoes the navigation to an account-only page for a guest: the URL
	 * goes back to the page that is still on screen (nothing re-renders).
	 * If there's no such page - e.g. the app was opened straight on a deep
	 * link like /cart - the home page is shown underneath the login modal.
	 */
	stayOnPreviousPage(blockedUrl) {
		const router = app("router");

		if (lastRenderedRoute && lastRenderedRoute.url !== blockedUrl) {
			history.replaceState(history.state, "", lastRenderedRoute.url);
			router.__resolved = lastRenderedRoute.resolved;
			restorePreviousActiveNav();
			return;
		}

		history.replaceState(history.state, "", "/");
		setTimeout(() => router.listen(), 0);
	}

	/**
	 * Ensures the user has completed their profile (filled in their phone number)
	 * @param {function} next
	 * @returns {any}
	 */
	profileCompletion(next) {
		const route = app("router");
		const user = gc.user.value;

		if (!user?.phone) {
			if (!route.is("user.edit-profile")) {
				let url = location.pathname;
				Preferences.set({ key: "redirect-url-after-auth", value: url });
				inform("Please complete your profile details (fill in your phone number) to continue", STRINGS.WARNING);
				route.to("user.edit-profile");
				return null;
			}
		}

		if (typeof next === "undefined") {
			return this;
		} else if (typeof next === "function") {
			return next();
		} else {
			return true;
		}
	}

	/**
	 * Ensures the user has at least one delivery address
	 * @param {function} next
	 * @returns {any}
	 */
	deliveryAddress(next) {
		if (!empty(uc.addresses.value)) {
			if (typeof next === "undefined") {
				return this;
			} else if (typeof next === "function") {
				return next();
			} else {
				return true;
			}
		}

		inform("Please add a delivery address to proceed", STRINGS.INFO);
		// Open the address form if no address exists
		app("broker").send(app("$e").deliveryAddress.needs.addressForm);
		return null;
	}
}
